import React, { useCallback, useEffect, useState } from 'react';
import pinterpolate from 'pinterpolate';
import { Redirect } from 'react-router-dom';

import { EMPLOYEE_PROFILE, UPDATE_EMPLOYEE } from 'constants/routes';

import initialFormData from 'constants/initialFormData';

import * as toast from 'utils/toast';
import { handleError } from 'utils/errorHandler';

import * as userService from 'services/user';
import * as roleService from 'services/role';
import * as departmentService from 'services/department';
import * as designationService from 'services/designation';
import * as designationAreaService from 'services/designationArea';
import * as engagementStatusService from 'services/engagementStatus';

import { useUserPermissions } from 'hooks/query/useUserInfo';
import { filterAllowedRoles } from 'utils/user';
import Loading from 'components/common/loading/Loading';
import EmployeeForm from '../components/employeeForm/EmployeeForm';

import type {
  AppraiserAllocation,
  CreateUpdateForm,
  LeapfrogHistory,
  ProfileInfo,
  UserPayloadInput,
} from 'interface/createUpdateForm';
import type { Role } from 'interface/role';
import type { EmployeeLike } from 'interface/userUtils';

interface NamedEntity {
  id: number;
  name: string;
}

interface PermissionRole {
  id: string;
  name?: string;
  [key: string]: string | number | boolean | undefined;
}

interface RedirectTarget {
  pageUrl: string;
  params: {
    id: number;
  };
}

interface PermissionsMap {
  [key: string]: boolean;
}

interface ApiError extends Error {
  response?: {
    data?: {
      error?: {
        message?: string;
      };
    };
  };
}

type EmployeeFormValues = CreateUpdateForm & ProfileInfo & LeapfrogHistory & AppraiserAllocation;
type EmployeeFormInput = EmployeeFormValues | typeof initialFormData;
type EmployeeImageValue = Array<{ file?: File }> | File | null | undefined;

const CreateForm: React.FC = () => {
  const permissions = useUserPermissions() as PermissionsMap;
  const [designations, setDesignations] = useState<NamedEntity[]>([]);
  const [employees] = useState<EmployeeLike[]>([]);
  const [departments, setDepartments] = useState<NamedEntity[]>([]);
  const [designationAreas, setDesignationAreas] = useState<NamedEntity[]>([]);
  const [engagementStatus, setEngagementStatus] = useState<NamedEntity[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formData] = useState<EmployeeFormInput>(initialFormData);
  const [toPage, setToPage] = useState<RedirectTarget | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);

  const fetchDepartments = useCallback(async (): Promise<NamedEntity[]> => {
    const response = await departmentService.fetchAll();
    const { data } = response as { data: Array<{ id: number; name: string }> };
    return data.map((item) => ({ id: item.id, name: item.name }));
  }, []);

  const fetchDesignations = useCallback(async (): Promise<Array<{ id: number; name: string }>> => {
    const response = await designationService.fetchAll();
    const { data } = response as { data: Array<{ id: number; name: string }> };
    return data.map((item) => ({ id: item.id, name: item.name }));
  }, []);

  const fetchDesignationAreas = useCallback(async (): Promise<NamedEntity[]> => {
    const response = await designationAreaService.fetchAll();
    const { data } = response as { data: Array<{ id: number; name: string }> };
    return data.map((item) => ({ id: item.id, name: item.name }));
  }, []);

  const fetchEngagementStatus = useCallback(async (): Promise<NamedEntity[]> => {
    const response = await engagementStatusService.fetchAll();
    const { data } = response as { data: Array<{ id: number; name: string }> };
    return data.map((item) => ({ id: item.id, name: item.name }));
  }, []);

  const fetchRoles = useCallback(async (): Promise<PermissionRole[]> => {
    const data = await roleService.fetchAll();
    if (!Array.isArray(data)) {
      return [];
    }
    return data.map((role) => ({ id: String(role.id), name: role.name }));
  }, []);

  const handleCreateClick = useCallback(async (submittedFormData: UserPayloadInput) => {
    setIsSubmitting(true);

    const { employeeImage: imageValue, ...restFormData } = submittedFormData as UserPayloadInput & {
      employeeImage?: EmployeeImageValue;
    };
    const createFormData = { ...restFormData };
    delete createFormData.empId;
    const employeeImageInput = imageValue as EmployeeImageValue;
    let employeeImage: File | null = null;
    if (employeeImageInput instanceof File) {
      employeeImage = employeeImageInput;
    } else if (Array.isArray(employeeImageInput)) {
      const firstImage = employeeImageInput[0];
      if (firstImage?.file instanceof File) {
        employeeImage = firstImage.file;
      }
    }

    try {
      const response = await userService.create({
        ...createFormData,
        middleName: createFormData.middleName || null,
      });
      const createdUserId = (response as { data: { id: number } }).data.id;

      if (createdUserId && employeeImage) {
        try {
          const imageUploadFormData = new FormData();
          imageUploadFormData.append('employeeImage', employeeImage);

          await userService.upload(createdUserId, imageUploadFormData);
          setToPage({
            pageUrl: EMPLOYEE_PROFILE,
            params: { id: createdUserId },
          });
        } catch {
          setToPage({
            pageUrl: UPDATE_EMPLOYEE,
            params: { id: createdUserId },
          });
          handleError(new Error('Image upload failed! Please reupload the profile image.'), {
            title: 'Error',
            message: 'Image upload failed! Please reupload the profile image.',
          });
        } finally {
          toast.success({
            title: 'Success',
            message: 'Employee has been created successfully.',
          });
        }
      }

      setIsSubmitting(false);
    } catch (error) {
      const formattedError = error as ApiError;
      if (formattedError.response?.data?.error) {
        formattedError.response.data.error.message = 'Oops! Something went wrong';
      }
      handleError(formattedError);
      setIsSubmitting(false);
    }
  }, []);

  useEffect(() => {
    window.document.title = 'Create new employee | Vyaguta';
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [designationsData, departmentsData, designationAreasData, engagementStatusData, rolesData] =
          await Promise.all([
            fetchDesignations(),
            fetchDepartments(),
            fetchDesignationAreas(),
            fetchEngagementStatus(),
            fetchRoles(),
          ]);

        setDepartments(departmentsData);
        setDesignations(designationsData);
        setDesignationAreas(designationAreasData);
        setEngagementStatus(engagementStatusData);
        setRoles(
          filterAllowedRoles(rolesData, permissions).map((item) => ({
            id: Number(item.id),
            name: typeof item.name === 'string' ? item.name : '',
          }))
        );
      } catch (error) {
        handleError(error);
      }

      setIsLoading(false);
    })();
  }, [fetchDepartments, fetchDesignationAreas, fetchDesignations, fetchEngagementStatus, fetchRoles, permissions]);

  if (isLoading) {
    return (
      <div className="container loading-container">
        <Loading />
      </div>
    );
  }

  if (toPage) {
    const togo = pinterpolate(toPage.pageUrl, toPage.params);
    return <Redirect to={togo} />;
  }

  return (
    <div>
      <EmployeeForm
        formData={formData}
        isCreateForm={true}
        isSubmitting={isSubmitting}
        handleSubmit={handleCreateClick}
        employees={employees}
        canEdit={() => true}
        departments={departments}
        designations={designations}
        designationAreas={designationAreas}
        engagementStatus={engagementStatus}
        roles={roles}
        skills={[]}
      />
    </div>
  );
};

export default CreateForm;
